import type { DocumentData, Query, Unsubscribe } from 'firebase/firestore'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import { useFirestore } from 'vuefire'
import { Command } from '@/models/Command'
import { RegulationListItem } from '@/models/RegulationListItem'
import { RegulationReport } from '@/models/RegulationReport'
import { ReportEntry } from '@/models/ReportEntry'
import type { ReviewDecision } from '@/models/ReportEntry'
import { ReportEntryV2 } from '@/models/ReportEntryV2'
import { ReportCardV3 } from '@/models/ReportCardV3'
import { ReportPositionV3, type LinePositionV3 } from '@/models/ReportPositionV3'
import { useUsersStore } from './users'
import { useRegistryFamiliesStore } from './registryFamilies'
import { expandReportEntriesV2ToCards } from '@/utils/regulationReportV2Expansion'
import { isReviewableEntryV2 } from '@/utils/regulationReportReviewable'
import { logger } from '@/utils/logger'
import { trackAction } from '@/composables/useActivityTracker'
import { CsmAction } from '@/constants/csmActions'
import { useAppStore } from './app'
import i18n from '@/plugins/i18n'
import { fetchReviewerNames } from '@/stores/reviewerNames'
import { isRunLive, reportListRows, type ReportListRow } from '@/utils/regulationReportList'
import { applyCategoryEdit, categoriesOf, categoryTagsPayload, sameCategories } from '@/utils/regulationCategories'
import { DEFAULT_LIST_COLUMNS, LIST_COLUMNS, type ListColumnKey } from '@/utils/regulationListColumns'

/** MYAC-1160 review pass (NOW-5) — which of the two review writes happened,
 *  so the confirmation toast can say the right thing. The card knows (the
 *  segmented control vs. the comment dialog's Save); the store cannot infer
 *  it from the payload, since a comment save re-sends the current decision. */
export type ReviewWriteOrigin = 'decision' | 'comment'

type WatchProgressStep = 'updating' | 'generating'

/** Mirrors RegulationListWriteMode in core_models_services (backend defaults to merge). */
export type RegulationListWriteMode = 'merge' | 'replace'

function parseWatchProgress(payload: Record<string, unknown>): {
  step: WatchProgressStep
  current: number
  total: number
} | null {
  const raw = payload?.progress
  if (!raw || typeof raw !== 'object') return null
  const p = raw as Record<string, unknown>
  return {
    step: (p.step as WatchProgressStep) ?? 'updating',
    current: typeof p.current === 'number' ? p.current : 0,
    total: typeof p.total === 'number' ? p.total : 0,
  }
}

/**
 * MYAC-1002 workstream B — one shared collator instead of
 * `String.prototype.localeCompare` per comparison. Same ordering (default
 * locale, default options), but the collator is built once for the session
 * rather than implicitly per call.
 */
const designationCollator = new Intl.Collator(undefined)

/** MYAC-1607 — the list and the report each keep their own category filter and
 *  grouping, per project, across route changes. */
export type CategoryViewSurface = 'list' | 'report'
export interface CategoryViewState {
  filter: string[]
  grouped: boolean
}
const EMPTY_CATEGORY_VIEW: CategoryViewState = Object.freeze({ filter: [], grouped: false }) as CategoryViewState
/** MYAC-1691 — one shared empty filter, so the getter never allocates one. */
const NO_FILTER: readonly string[] = Object.freeze([])


/** The categories a recreated row keeps (a designation fix, an edition pick, a
 *  successor followed). Nothing at all for a row without categories, so these
 *  writes stay inside the create allow-list they had before MYAC-1607. */
function carriedCategories(row: RegulationListItem): Partial<ReturnType<typeof categoryTagsPayload>> {
  const categories = categoriesOf(row.tags)
  return categories.length > 0 ? categoryTagsPayload(categories) : {}
}

function sortByDesignation(items: RegulationListItem[]): RegulationListItem[] {
  return items.sort((a, b) => designationCollator.compare(a.designation || '', b.designation || ''))
}

// Module-level unsub handles — kept outside reactive state to avoid Proxy issues
let _regulationListUnsub: Unsubscribe | null = null
let _reportsUnsub: Unsubscribe | null = null
let _entriesUnsub: Unsubscribe | null = null
let _entriesV2Unsub: Unsubscribe | null = null
let _cardsV3Unsub: Unsubscribe | null = null
let _positionsV3Unsub: Unsubscribe | null = null
let _extractCmdUnsub: Unsubscribe | null = null
let _watchCmdUnsub: Unsubscribe | null = null
// MYAC-1514 — the project's runs being generated, and its failed ones.
let _runsPendingUnsub: Unsubscribe | null = null
let _runsFailedUnsub: Unsubscribe | null = null

export const useRegulatoryMonitoringStore = defineStore('regulatoryMonitoring', {
  state: () => ({
    _current_pid: '' as string,
    regulations: [] as RegulationListItem[],
    reports: [] as RegulationReport[],
    reportEntries: [] as ReportEntry[],
    // v2 pipeline entries (MYAC-904 Phase 4) — separate state from the legacy
    // `reportEntries` above. Populated by `subscribeEntriesV2`, which the
    // report view calls INSTEAD OF `subscribeEntries` when the displayed
    // report's `schema_version === 'v2'` (single dispatch, never both).
    reportEntriesV2: [] as ReportEntryV2[],
    // MYAC-1496 lot 1 — the cards of a v3 report, filled by `subscribeCardsV3`
    // (the report view calls it INSTEAD of the v1/v2 entry subscriptions
    // when `schema_version === 'v3'`).
    reportCardsV3: [] as ReportCardV3[],
    cardsV3Status: 'idle' as 'idle' | 'loading' | 'ready' | 'error',
    // MYAC-1496 lot 1, step 6 — the positions given in the displayed v3
    // report, by row id (server-written, read here).
    reportPositionsV3: {} as Record<string, LinePositionV3>,
    positionsV3Status: 'idle' as 'idle' | 'loading' | 'ready' | 'error',
    _extractCommand: null as Command | null,
    _watchCommand: null as Command | null,
    /** MYAC-1514 — every `regulation_watch` run of the project that is pending
     *  or failed, whoever started it (this page, another tab, the schedule):
     *  the report list shows them as rows. */
    watchRuns: [] as Command[],
    // MYAC-904 change 2 ("hide it just in case") — snapshotted once, in
    // `_listenToWatchCommand`, right before we start observing a pending
    // watch command: the report that was displayed just before THIS
    // generation started (or `null` if there wasn't one). Read by
    // `displayedReport` while the command is pending, so the page never
    // auto-switches to the streaming (half-written) new report. Cleared in
    // `_cleanupWatchCommandSub`.
    _reportIdBeforeGeneration: null as string | null,
    selectedReportId: null as string | null,
    isLoadingLists: false,
    isLoadingReports: false,
    isLoadingEntries: false,
    /** uid -> display name, filled on demand by `resolveReviewerNames` and
     *  read by the report view for the « Revu par … » line. `null` is a
     *  tombstone for a uid that could not be resolved (unreadable user doc)
     *  — MYAC-1160 M8: without it, an unresolvable uid was never cached and
     *  every re-fire of `resolveReviewerNames` re-issued its `getDoc`. */
    reviewerNames: {} as Record<string, string | null>,
    /** MYAC-1607 — keyed `${pid}:${surface}`; see `categoryView`. */
    categoryViews: {} as Record<string, CategoryViewState>,
    /** MYAC-1607 — the columns each project's list shows; see `listColumnsFor`. */
    listColumns: {} as Record<string, readonly ListColumnKey[]>,
    /** MYAC-1691 — the projects each project's list is filtered to; see `listProjectsFilterFor`. */
    listProjectsFilters: {} as Record<string, readonly string[]>,
  }),
  getters: {
    /** MYAC-1607 — the category filter and grouping of one surface of a project. */
    categoryView: (state) => (pid: string, surface: CategoryViewSurface): CategoryViewState =>
      state.categoryViews[`${pid}:${surface}`] ?? EMPTY_CATEGORY_VIEW,
    /** MYAC-1607 — the optional columns a project's list shows, in table order. */
    listColumnsFor: (state) => (pid: string): readonly ListColumnKey[] => state.listColumns[pid] ?? DEFAULT_LIST_COLUMNS,
    /** MYAC-1691 — the values a project's list is filtered to in its « Projects
     *  concerned » column (the list owns their meaning); none by default. */
    listProjectsFilterFor: (state) => (pid: string): readonly string[] => state.listProjectsFilters[pid] ?? NO_FILTER,
    latestReport(state): RegulationReport | null {
      return state.reports[0] ?? null
    },
    displayedReport(state): RegulationReport | null {
      if (!state.reports.length) return null
      if (state.selectedReportId) {
        return state.reports.find((r) => r.id === state.selectedReportId) ?? null
      }
      // MYAC-904 change 2 ("hide it just in case") — while a generation is
      // in progress, never auto-switch to the streaming (half-written) new
      // report: keep showing whatever was displayed before THIS generation
      // started (or the empty state if there wasn't one). This guards
      // SELECTION only — `subscribeReports`'s listener is untouched, the
      // new report still lands in `state.reports` as soon as it's written,
      // we just don't pick it here. Since MYAC-1514 the report page always
      // selects its report by URL (the branch above), and the finished run
      // takes its row in the list instead of switching anything.
      if (state._watchCommand?.isPending) {
        if (!state._reportIdBeforeGeneration) return null
        return state.reports.find((r) => r.id === state._reportIdBeforeGeneration) ?? null
      }
      return state.reports[0] ?? null
    },
    /** MYAC-1307 — the newest FINISHED report, for the list page's line to the
     *  report. Unlike `displayedReport` it ignores a report picked in the
     *  report page's history (the list always points at the latest), and it
     *  applies the same guard while a generation runs: the half-written new
     *  report is never the one linked to. */
    latestCompletedReport(state): RegulationReport | null {
      if (state._watchCommand?.isPending) {
        if (!state._reportIdBeforeGeneration) return null
        return state.reports.find((r) => r.id === state._reportIdBeforeGeneration) ?? null
      }
      return state.reports[0] ?? null
    },
    extractCommand(state): Command | null {
      return state._extractCommand
    },
    watchCommand(state): Command | null {
      return state._watchCommand
    },
    isExtracting(state): boolean {
      return state._extractCommand?.isPending ?? false
    },
    /** A run is really going on, started here or anywhere else (MYAC-1514);
     *  a run interrupted without a word does not hold « Générer » forever. */
    isWatching(state): boolean {
      const now = new Date()
      if (state._watchCommand?.isPending && isRunLive(state._watchCommand as Command, now)) return true
      return state.watchRuns.some((run) => isRunLive(run as Command, now))
    },
    /** MYAC-1514 — the « Rapport de veille » list, newest first. */
    reportRows(state): ReportListRow[] {
      return reportListRows(state.reports as RegulationReport[], state.watchRuns as Command[])
    },
    watchProgress(state): { step: WatchProgressStep; current: number; total: number; percentage: number } | null {
      if (!state._watchCommand?.isPending) return null
      const progress = parseWatchProgress(state._watchCommand.payload)
      if (!progress) return null
      const stepPct = progress.total > 0 ? progress.current / progress.total : 0
      const percentage = progress.step === 'updating'
        ? Math.floor(stepPct * 75)
        : Math.floor(75 + stepPct * 25)
      return {
        step: progress.step,
        current: progress.current,
        total: progress.total,
        percentage,
      }
    },
    withdrawnEntries(state): ReportEntry[] {
      return state.reportEntries.filter((e) => e.category === 'withdrawn')
    },
    publishedEntries(state): ReportEntry[] {
      return state.reportEntries.filter((e) => e.category === 'published')
    },
    upcomingEntries(state): ReportEntry[] {
      return state.reportEntries
        .filter((e) => e.category === 'upcoming')
        .sort((a, b) => (a.parsed_date ?? '').localeCompare(b.parsed_date ?? ''))
    },
    reviewProgress(state): { reviewed: number; total: number } {
      const reviewable = state.reportEntries.filter((e) => e.category !== 'upcoming')
      const reviewed = reviewable.filter((e) => e.review_decision !== 'pending').length
      return { reviewed, total: reviewable.length }
    },
    // ── v2 getters (MYAC-904 Phase 4) ─────────────────────────────────────
    // "Identified" entries only (identification === 'identified') — the
    // in-progress placeholders are surfaced separately via
    // `identifyingEntriesV2` so they get their own "Identifying…" card state
    // instead of being force-fit into a category section.
    withdrawnEntriesV2(state): ReportEntryV2[] {
      return state.reportEntriesV2.filter((e) => e.identification === 'identified' && !e.family_rejection && e.category === 'withdrawn')
    },
    publishedEntriesV2(state): ReportEntryV2[] {
      return state.reportEntriesV2.filter((e) => e.identification === 'identified' && !e.family_rejection && e.category === 'published')
    },
    upcomingEntriesV2(state): ReportEntryV2[] {
      return state.reportEntriesV2
        .filter((e) => e.identification === 'identified' && !e.family_rejection && e.category === 'upcoming')
        .sort((a, b) => (a.parsed_date ?? '').localeCompare(b.parsed_date ?? ''))
    },
    // MYAC-904 re-partition: the row pinned an edition the registry cannot
    // place at all. Actionable (pick a real edition), never a silent
    // fallback to someone else's revision behind an "en attente" chip.
    unrecognisedEntriesV2(state): ReportEntryV2[] {
      return state.reportEntriesV2.filter((e) => e.identification === 'identified' && !e.family_rejection && e.category === 'unrecognised')
    },
    // MYAC-904 re-partition: an EN/regional adoption of the subscribed text
    // exists. Same technical content, its OWN dates of application — so
    // actionable, but never the red "obsolete" treatment.
    adoptedEntriesV2(state): ReportEntryV2[] {
      return state.reportEntriesV2.filter((e) => e.identification === 'identified' && !e.family_rejection && e.category === 'adopted')
    },
    // v2-only persisted "no changes" category (MYAC-861) — replaces the
    // legacy `list - entries` string derivation entirely for v2 reports.
    unchangedEntriesV2(state): ReportEntryV2[] {
      return state.reportEntriesV2.filter((e) => e.identification === 'identified' && !e.family_rejection && e.category === 'unchanged')
    },
    // Identity-level rejection (MYAC-904, 2026-08-12): entries whose family
    // the registry terminally answered as not-a-trackable-instrument —
    // their own "Non suivies" section, regardless of stored category (the
    // rejection is the fact that matters; category on these entries is a
    // placeholder). Excluded from every category getter above.
    // MYAC-1160 M7 — truthy, not `!== null`, matching `isReviewableEntryV2`'s
    // predicate (`regulationReportReviewable.ts`): some callers' fixtures
    // leave `family_rejection` `undefined` rather than `null` for "not
    // rejected", which `!== null` alone would misclassify as rejected.
    rejectedEntriesV2(state): ReportEntryV2[] {
      return state.reportEntriesV2.filter((e) => e.identification === 'identified' && Boolean(e.family_rejection))
    },
    // Rows that resolved to no family yet (spec invariant #3: every row
    // appears — never a silent drop).
    identifyingEntriesV2(state): ReportEntryV2[] {
      return state.reportEntriesV2.filter(
        (e) =>
          e.identification !== 'identified' &&
          e.identification !== 'not_found' &&
          e.identification !== 'needs_review',
      )
    },
    // MYAC-1002 (2026-08-12): rows the resolve lane TERMINALLY rejected. They
    // used to sit in `identifyingEntriesV2`, under a section promising they
    // would appear "once identified" — false, and contradicting the norms
    // table, which already shows them as "Introuvable".
    notFoundEntriesV2(state): ReportEntryV2[] {
      return state.reportEntriesV2.filter((e) => e.identification === 'not_found')
    },
    // MYAC-1002 (2026-08-13): rows the resolve lane ESCALATED — parked for a
    // human, nothing retries them. Neither "identifying" (that band promises
    // progress) nor terminal (a human verdict re-files them).
    needsReviewEntriesV2(state): ReportEntryV2[] {
      return state.reportEntriesV2.filter((e) => e.identification === 'needs_review')
    },
    // MYAC-904 report-v2 polish ("one card per subscribed line") — the
    // denominator is the EXPANDED (per-row) reviewable count, so "x/281
    // revus" reconciles with the section cards, not the family-deduped
    // entry count. Same expansion util as the web cards / PDF export.
    // MYAC-1160 review pass (NOW-1) — which entries are reviewable is no
    // longer restated here: `isReviewableEntryV2` is the one rule, shared
    // with the card. The inline version ("identified, not upcoming, not
    // unchanged") also counted entries whose category is `unknown`, which
    // `visibleSections` renders in no section at all — 116 for 114 cards.
    reviewProgressV2(state): { reviewed: number; total: number } {
      const reviewable = state.reportEntriesV2.filter(isReviewableEntryV2)
      const rowsById = new Map(state.regulations.map((r) => [r.id, r]))
      const cards = expandReportEntriesV2ToCards(reviewable, rowsById)
      const reviewed = cards.filter((c) => c.entry.review_decision !== 'pending').length
      return { reviewed, total: cards.length }
    },
  },
  actions: {
    _cleanupRegulationListSub() {
      if (_regulationListUnsub) {
        try { _regulationListUnsub() } catch { /* ignore */ }
        _regulationListUnsub = null
      }
      this.regulations = []
    },
    _cleanupReportsSub() {
      if (_reportsUnsub) {
        try { _reportsUnsub() } catch { /* ignore */ }
        _reportsUnsub = null
      }
      this.reports = []
    },
    _cleanupEntriesSub() {
      if (_entriesUnsub) {
        try { _entriesUnsub() } catch { /* ignore */ }
        _entriesUnsub = null
      }
      this.reportEntries = []
    },
    _cleanupEntriesV2Sub() {
      if (_entriesV2Unsub) {
        try { _entriesV2Unsub() } catch { /* ignore */ }
        _entriesV2Unsub = null
      }
      this.reportEntriesV2 = []
    },
    _cleanupExtractCommandSub() {
      if (_extractCmdUnsub) {
        try { _extractCmdUnsub() } catch { /* ignore */ }
        _extractCmdUnsub = null
      }
      this._extractCommand = null
    },
    _cleanupWatchCommandSub() {
      if (_watchCmdUnsub) {
        try { _watchCmdUnsub() } catch { /* ignore */ }
        _watchCmdUnsub = null
      }
      this._watchCommand = null
      this._reportIdBeforeGeneration = null
    },

    _listenToExtractCommand(pid: string, commandId: string) {
      const firestore = useFirestore()
      const commandDocRef = doc(
        firestore,
        `projects/${pid}/command_queues/extract_regulations_list/commands/${commandId}`,
      )
      let previousStatus: string | null = null
      _extractCmdUnsub = onSnapshot(
        commandDocRef,
        (snap) => {
          const command = Command.fromFirebase(snap)
          this._extractCommand = command
          if (!command) return
          const currentStatus = command.status
          if (previousStatus !== currentStatus) {
            if (currentStatus === 'done') {
              // MYAC-1306 (C3) — no success toast: the list page shows a
              // persistent, closable line with the row count and the file
              // name instead (`import-done-line`). Failures keep their toast.
              setTimeout(() => this._cleanupExtractCommandSub(), 2000)
            } else if (currentStatus === 'failed') {
              const errorMsg = command.error || i18n.global.t('regulatory_monitoring.extract_error')
              useAppStore().toast(
                i18n.global.t('regulatory_monitoring.extract_error_title'),
                errorMsg,
                'error',
              )
              setTimeout(() => this._cleanupExtractCommandSub(), 2000)
            }
          }
          previousStatus = currentStatus
        },
        (error) => {
          logger.error('extract_regulations_list command subscribe error', error)
          useAppStore().toast(
            i18n.global.t('regulatory_monitoring.extract_error_title'),
            i18n.global.t('regulatory_monitoring.extract_error'),
            'error',
          )
          this._cleanupExtractCommandSub()
        },
      )
    },

    _listenToWatchCommand(pid: string, commandId: string) {
      // MYAC-904 change 2 — snapshot BEFORE the first snapshot of this
      // command ever lands, i.e. before this generation could possibly have
      // written anything: whatever `displayedReport` resolves to right now
      // is "the previously completed report" for the whole duration of this
      // watch session (see `displayedReport`).
      this._reportIdBeforeGeneration = this.displayedReport?.id ?? null
      const firestore = useFirestore()
      const commandDocRef = doc(
        firestore,
        `projects/${pid}/command_queues/regulation_watch/commands/${commandId}`,
      )
      let previousStatus: string | null = null
      _watchCmdUnsub = onSnapshot(
        commandDocRef,
        (snap) => {
          const command = Command.fromFirebase(snap)
          this._watchCommand = command
          if (!command) return
          const currentStatus = command.status
          if (previousStatus !== currentStatus) {
            if (currentStatus === 'done') {
              // MYAC-1514: the new report takes its row at the top of the
              // list; nothing switches under the page the person is on.
              useAppStore().toast(
                i18n.global.t('regulatory_monitoring.watch_success_title'),
                i18n.global.t('regulatory_monitoring.watch_success'),
                'success',
              )
              setTimeout(() => this._cleanupWatchCommandSub(), 2000)
            } else if (currentStatus === 'failed') {
              const errorMsg = command.error || i18n.global.t('regulatory_monitoring.watch_error')
              useAppStore().toast(
                i18n.global.t('regulatory_monitoring.watch_error_title'),
                errorMsg,
                'error',
              )
              setTimeout(() => this._cleanupWatchCommandSub(), 2000)
            }
          }
          previousStatus = currentStatus
        },
        (error) => {
          logger.error('regulation_watch command subscribe error', error)
          useAppStore().toast(
            i18n.global.t('regulatory_monitoring.watch_error_title'),
            i18n.global.t('regulatory_monitoring.watch_error'),
            'error',
          )
          this._cleanupWatchCommandSub()
        },
      )
    },

    cleanup() {
      this._cleanupRegulationListSub()
      this._cleanupReportsSub()
      this._cleanupEntriesSub()
      this._cleanupEntriesV2Sub()
      this._cleanupCardsV3Sub()
      this._cleanupExtractCommandSub()
      this._cleanupWatchCommandSub()
      this._cleanupWatchRunsSub()
      this._current_pid = ''
      this.selectedReportId = null
    },

    /**
     * MYAC-1522 (onboarding pass, decision D) — how many texts a watch follows, for
     * the welcome letter of a watch-only company: one count, never the whole list.
     * Null when the read fails (the letter then gives no number).
     */
    async countWatchedTexts(pid: string): Promise<number | null> {
      if (!pid) return null
      try {
        const snap = await getCountFromServer(collection(useFirestore(), `projects/${pid}/regulation_lists`))
        return snap.data().count
      } catch (error) {
        logger.error('countWatchedTexts failed', error)
        return null
      }
    },

    async initForProject(pid: string, force = false) {
      if (!pid) {
        this.cleanup()
        return
      }
      if (!force && this._current_pid === pid && _regulationListUnsub && _reportsUnsub) {
        return
      }
      if (this._current_pid !== pid) {
        this.cleanup()
      }
      this._current_pid = pid
      await Promise.all([
        this.subscribeRegulationList(pid),
        this.subscribeReports(pid),
        this.subscribeWatchRuns(pid),
        this._restorePendingExtractCommand(pid),
        this._restorePendingWatchCommand(pid),
      ])
    },

    async _restorePendingExtractCommand(pid: string): Promise<void> {
      if (_extractCmdUnsub) return
      try {
        const firestore = useFirestore()
        const commandsRef = collection(
          firestore,
          `projects/${pid}/command_queues/extract_regulations_list/commands`,
        )
        const q = query(commandsRef, where('status', 'in', ['requested', 'in_progress']), limit(1))
        const snap = await getDocs(q)
        if (snap.empty) return
        this._listenToExtractCommand(pid, snap.docs[0].id)
      } catch (error) {
        logger.error('Failed to restore pending extract command', error)
      }
    },

    async _restorePendingWatchCommand(pid: string): Promise<void> {
      if (_watchCmdUnsub) return
      try {
        const firestore = useFirestore()
        const commandsRef = collection(
          firestore,
          `projects/${pid}/command_queues/regulation_watch/commands`,
        )
        const q = query(commandsRef, where('status', 'in', ['requested', 'in_progress']), limit(1))
        const snap = await getDocs(q)
        if (snap.empty) return
        this._listenToWatchCommand(pid, snap.docs[0].id)
      } catch (error) {
        logger.error('Failed to restore pending watch command', error)
      }
    },

    /**
     * MYAC-1002 workstream B (list performance) — the snapshot handler is
     * INCREMENTAL: it applies `snap.docChanges()` to a doc-id keyed map
     * instead of re-parsing `snap.docs` wholesale.
     *
     * Why it matters far beyond the parse cost: `RegulationListItem.fromFirebase`
     * goes through class-transformer's `plainToInstance` (reflection), and the
     * old code minted a BRAND-NEW instance for every row on every snapshot. The
     * norms table's per-row memoization (`RowCache`) keys on the row object's
     * REFERENCE, so a single row's `last_reviewed_at` write — or the resolver
     * stamping one `registry_ref` — invalidated all ~312 rows and rebuilt the
     * whole display set (chip state machine + enrichment + status/edition
     * cells), then re-rendered every DataTable row. Keeping unchanged rows'
     * references stable turns "one row changed" into exactly one rebuilt row.
     *
     * `docChanges()` is authoritative for reconstructing the set: the first
     * snapshot reports every doc as `added`, and each later snapshot's changes
     * are the delta from the previous one on THIS listener (metadata-only
     * changes are excluded — we never pass `includeMetadataChanges`). The map
     * lives in this closure, so it is discarded with the listener.
     *
     * NOT changed here (deliberate): this is still a whole-collection
     * `onSnapshot` with client-side filtering/pagination. Server-side paging
     * would be a rewrite of the table (the filters, the export, the report
     * deep-link and `reviewProgressV2` all read the full list), and the wire
     * cost is already delta-only — the cost was CPU, which is what this fixes.
     */
    subscribeRegulationList(pid: string): Promise<void> {
      if (!pid) return Promise.resolve()
      this._cleanupRegulationListSub()
      this.isLoadingLists = true
      const firestore = useFirestore()
      const ref = collection(firestore, `projects/${pid}/regulation_lists`)
      const byId = new Map<string, RegulationListItem>()
      return new Promise<void>((resolve) => {
        let resolved = false
        _regulationListUnsub = onSnapshot(
          ref,
          (snap) => {
            const changes = snap.docChanges()
            // `!resolved` keeps the very first snapshot authoritative even when
            // it carries no changes (an empty collection), so `regulations` is
            // always assigned once per subscription.
            if (changes.length > 0 || !resolved) {
              for (const change of changes) {
                if (change.type === 'removed') {
                  byId.delete(change.doc.id)
                  continue
                }
                const item = RegulationListItem.fromFirebase(change.doc)
                // MYAC-1002 load-perf: rows are immutable value objects — every
                // update arrives as a NEW instance via this snapshot handler,
                // and no code mutates a row in place. markRaw spares Vue from
                // deep-proxying ~1000 class instances (measured: the proxy +
                // dependency churn dominated the load-phase main-thread cost).
                if (item) byId.set(change.doc.id, markRaw(item))
                else byId.delete(change.doc.id)
              }
              this.regulations = sortByDesignation(Array.from(byId.values()))
            }
            this.isLoadingLists = false
            if (!resolved) { resolved = true; resolve() }
          },
          (error) => {
            logger.error('regulatoryMonitoring regulation_lists subscribe error', error)
            this.isLoadingLists = false
            if (!resolved) { resolved = true; resolve() }
          },
        )
      })
    },

    subscribeReports(pid: string): Promise<void> {
      if (!pid) return Promise.resolve()
      this._cleanupReportsSub()
      this.isLoadingReports = true
      const firestore = useFirestore()
      const ref = collection(firestore, `projects/${pid}/regulation_reports`)
      return new Promise<void>((resolve) => {
        let resolved = false
        _reportsUnsub = onSnapshot(
          ref,
          (snap) => {
            const list = snap.docs
              .map((d) => RegulationReport.fromFirebase(d))
              .filter((r): r is RegulationReport => r !== null)
            this.reports = list.sort((a, b) => {
              const ta = a.generated_at?.getTime() ?? a.created_at?.getTime() ?? 0
              const tb = b.generated_at?.getTime() ?? b.created_at?.getTime() ?? 0
              return tb - ta
            })
            this.isLoadingReports = false
            if (!resolved) { resolved = true; resolve() }
          },
          (error) => {
            logger.error('regulatoryMonitoring regulation_reports subscribe error', error)
            this.isLoadingReports = false
            if (!resolved) { resolved = true; resolve() }
          },
        )
      })
    },

    _cleanupWatchRunsSub() {
      for (const unsub of [_runsPendingUnsub, _runsFailedUnsub]) {
        if (unsub) {
          try { unsub() } catch { /* ignore */ }
        }
      }
      _runsPendingUnsub = null
      _runsFailedUnsub = null
      this.watchRuns = []
    },

    /**
     * MYAC-1514 — the runs the report list shows: pending ones (a handful at
     * most) and failed ones (two on prod in all), each with one equality or
     * `in` filter, so no composite index. Merged by id: a run moves from one
     * query to the other as its status changes.
     */
    subscribeWatchRuns(pid: string): Promise<void> {
      if (!pid) return Promise.resolve()
      this._cleanupWatchRunsSub()
      const firestore = useFirestore()
      const commandsRef = collection(firestore, `projects/${pid}/command_queues/regulation_watch/commands`)
      const byQuery: Record<'pending' | 'failed', Command[]> = { pending: [], failed: [] }
      const publish = () => {
        const seen = new Set<string>()
        this.watchRuns = [...byQuery.pending, ...byQuery.failed].filter((c) => !seen.has(c.id) && !!seen.add(c.id))
      }
      // The handles are kept the moment the listeners exist, so a project
      // switch before the first snapshots still stops them (review of PR 865).
      const listen = (which: 'pending' | 'failed', q: Query<DocumentData>, keep: (unsub: Unsubscribe) => void) =>
        new Promise<void>((resolve) => {
          let resolved = false
          keep(onSnapshot(
            q,
            (snap) => {
              byQuery[which] = snap.docs.map((d) => Command.fromFirebase(d)).filter((c): c is Command => c !== null)
              publish()
              if (!resolved) { resolved = true; resolve() }
            },
            (error) => {
              // The list still shows the reports; only the run rows are missing.
              logger.error(`regulation_watch ${which} runs subscribe error`, error)
              if (!resolved) { resolved = true; resolve() }
            },
          ))
        })
      return Promise.all([
        listen('pending', query(commandsRef, where('status', 'in', ['requested', 'in_progress'])), (u) => { _runsPendingUnsub = u }),
        listen('failed', query(commandsRef, where('status', '==', 'failed')), (u) => { _runsFailedUnsub = u }),
      ]).then(() => undefined)
    },

    selectReport(reportId: string | null) {
      this.selectedReportId = reportId
    },

    /**
     * Load a single report document (e.g. if not yet in snapshot).
     */
    async loadReport(pid: string, reportId: string): Promise<RegulationReport | null> {
      if (!pid || !reportId) return null
      const firestore = useFirestore()
      const ref = doc(firestore, `projects/${pid}/regulation_reports`, reportId)
      const snap = await getDoc(ref)
      if (!snap.exists()) return null
      return RegulationReport.fromFirebase(snap)
    },

    /**
     * Date of the most recent completed extraction for a document, or null if
     * none. Used to detect a re-upload of an already-extracted file (content
     * dedup returns the original document_id), so the UI can ask the user
     * whether to re-extract or skip instead of the backend silently no-op'ing.
     */
    async getLastExtractionDate(pid: string, documentId: string): Promise<Date | null> {
      if (!pid || !documentId) return null
      try {
        const firestore = useFirestore()
        const commandsRef = collection(
          firestore,
          `projects/${pid}/command_queues/extract_regulations_list/commands`,
        )
        const q = query(
          commandsRef,
          where('payload.document_id', '==', documentId),
          where('status', '==', 'done'),
        )
        const snap = await getDocs(q)
        let latest: Date | null = null
        snap.forEach((d) => {
          const raw = (d.data().finished_at ?? d.data().updated_at) as
            | { toDate?: () => Date }
            | undefined
          const when = raw?.toDate ? raw.toDate() : null
          if (when && (!latest || when > latest)) latest = when
        })
        return latest
      } catch (error) {
        logger.error('getLastExtractionDate failed', error)
        return null
      }
    },

    async triggerExtractRegulationsList(
      pid: string,
      datasourceId: string | null,
      documentId: string | null = null,
      mode: RegulationListWriteMode = 'merge',
    ): Promise<void> {
      if (!pid) {
        logger.error('triggerExtractRegulationsList: missing pid')
        return
      }
      if (!datasourceId) {
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.extract_error_title'),
          i18n.global.t('regulatory_monitoring.no_datasource'),
          'error',
        )
        return
      }

      this._cleanupExtractCommandSub()

      try {
        const firestore = useFirestore()
        const commandsRef = collection(
          firestore,
          `projects/${pid}/command_queues/extract_regulations_list/commands`,
        )

        const docRef = await addDoc(commandsRef, {
          status: 'requested',
          payload: {
            kind: 'extract_regulations_list',
            document_id: documentId,
            datasource_id: datasourceId,
            mode,
          },
          requested_by: null,
        })

        this._listenToExtractCommand(pid, docRef.id)
        try { trackAction(CsmAction.RegulationNormsUploaded, { projectId: pid }) } catch { }
      } catch (error) {
        logger.error('Failed to trigger extract_regulations_list', error)
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.extract_error_title'),
          i18n.global.t('regulatory_monitoring.extract_error'),
          'error',
        )
      }
    },

    /**
     * Create a new regulation row in regulation_lists (auto-generated ID).
     */
    /**
     * True when another regulation_lists doc already has this designation.
     * Matches the extraction worker's upsert semantics (exact designation).
     */
    async _designationExists(
      pid: string,
      designation: string,
      excludeId: string | null = null,
    ): Promise<boolean> {
      const firestore = useFirestore()
      const ref = collection(firestore, `projects/${pid}/regulation_lists`)
      const snap = await getDocs(
        query(ref, where('designation', '==', designation), limit(2)),
      )
      return snap.docs.some((d) => d.id !== excludeId)
    },

    async createRegulation(
      pid: string,
      data: {
        designation: string
        title: string
        normative_body: string
      },
    ): Promise<void> {
      if (!pid) {
        logger.error('createRegulation: missing pid')
        return
      }
      // Capture exactly what the user typed before any trimming/normalization is applied
      // to the `designation` field written below (resolver input, MYAC-904).
      const asTyped = data.designation
      const designation = data.designation.trim()
      if (await this._designationExists(pid, designation)) {
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.duplicate_designation_title'),
          i18n.global.t('regulatory_monitoring.duplicate_designation', {
            designation,
          }),
          'warn',
        )
        throw new Error('duplicate designation')
      }
      try {
        const firestore = useFirestore()
        const ref = collection(firestore, `projects/${pid}/regulation_lists`)
        await addDoc(ref, {
          designation,
          title: data.title,
          normative_body: data.normative_body,
          as_typed: asTyped,
          source: 'manual',
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        })
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.create_success_title'),
          i18n.global.t('regulatory_monitoring.create_success'),
          'success',
        )
        try { trackAction(CsmAction.RegulationNormAdded, { projectId: pid, projectMeta: { norm_count: this.regulations.length, kind: 'manual' } }) } catch { }
      } catch (error) {
        logger.error('createRegulation failed', error)
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.create_error_title'),
          i18n.global.t('regulatory_monitoring.create_error'),
          'error',
        )
        throw error
      }
    },

    // MYAC-1002 item 4 carry-over — `updateRegulation` is GONE. It lost its
    // last caller when "Modifier" was removed from the norms row-actions menu
    // (PR #358): it only ever wrote `title`/`normative_body` (designation is
    // immutable, MYAC-904 d315dd8), both of which the registry enriches
    // anyway. Its four `regulatory_monitoring.edit_{success,error}*` toast
    // strings went with it (EN + FR) — nothing else read them. NormForm keeps
    // its edit MODE (a pure form component, still covered by its own tests).

    /**
     * MYAC-904 "did you mean…?" accept (plan §12, decision d315dd8): designation
     * is IMMUTABLE once a row exists, so "correcting" it to a suggested family
     * is a delete-and-recreate under the corrected designation — the always-on
     * `regulation_lists/created` trigger re-resolves it (instantly here, since
     * the suggested family already exists). last_reviewed_at is deliberately
     * NOT carried over: the new row is a fresh identity, per the decision.
     * Mirrors createRegulation's exact write shape (as_typed pre-trim, source:
     * 'manual', no registry_ref/resolver_hint) so the rules allow-list holds.
     */
    async replaceRegulationDesignation(
      pid: string,
      regulationId: string,
      newDesignation: string,
    ): Promise<void> {
      if (!pid || !regulationId || !newDesignation) {
        logger.error('replaceRegulationDesignation: missing pid, regulationId, or newDesignation')
        return
      }
      const row = this.regulations.find((r) => r.id === regulationId)
      if (!row) {
        logger.error('replaceRegulationDesignation: row not found', regulationId)
        return
      }
      // Capture exactly what was picked before any trimming — mirrors
      // createRegulation's asTyped/designation split (resolver input, MYAC-904).
      const asTyped = newDesignation
      const designation = newDesignation.trim()
      if (await this._designationExists(pid, designation, regulationId)) {
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.duplicate_designation_title'),
          i18n.global.t('regulatory_monitoring.duplicate_designation', {
            designation,
          }),
          'warn',
        )
        throw new Error('duplicate designation')
      }
      try {
        const firestore = useFirestore()
        const listRef = collection(firestore, `projects/${pid}/regulation_lists`)
        const oldRef = doc(firestore, `projects/${pid}/regulation_lists`, regulationId)
        const newRef = doc(listRef)
        const batch = writeBatch(firestore)
        batch.delete(oldRef)
        batch.set(newRef, {
          designation,
          title: row.title,
          normative_body: row.normative_body,
          as_typed: asTyped,
          source: 'manual',
          ...carriedCategories(row),
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        })
        await batch.commit()
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.replace_designation_success_title'),
          i18n.global.t('regulatory_monitoring.replace_designation_success'),
          'success',
        )
      } catch (error) {
        logger.error('replaceRegulationDesignation failed', error)
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.replace_designation_error_title'),
          i18n.global.t('regulatory_monitoring.replace_designation_error'),
          'error',
        )
        throw error
      }
    },

    /**
     * MYAC-904 P4.3 D5 — apply an edition-picker selection. Modeled EXACTLY
     * on `replaceRegulationDesignation` above: designation is immutable, so
     * pinning a specific revision is ALSO a delete+recreate under the SAME
     * designation/title/normative_body/as_typed, done in ONE writeBatch —
     * never an in-place field update. The new doc additionally carries
     * `resolver_hint: {family_id, revision_key}`, which the resolver honors
     * first and stamps with `revision_source: 'user'` (a confirmed pick, vs.
     * the resolver's own `'default'` snapshot).
     *
     * `last_reviewed_at` is deliberately NEVER carried forward: the
     * `regulation_lists` create rule's field allow-list (firebase
     * firestore.rules) does not include `last_reviewed_at` on `create` (only
     * on `update`) — writing it here would be rejected. The confirm dialog
     * (norms view) tells the user the review history resets, same as the
     * did-you-mean accept flow already does implicitly.
     *
     * The duplicate-designation guard (`_designationExists`, excluding this
     * row's own id) does NOT block this call even though the designation is
     * unchanged — it only ever flags a DIFFERENT row with the same
     * designation, and the delete+create happens in one batch, so there is
     * never a moment with two rows sharing it.
     */
    /**
     * MYAC-904 in-place edition CONFIRM — for when the picker selection IS
     * the edition the row already follows (confirming a resolver default
     * pin). Unlike `pinRegulationRevision` below this is a plain row UPDATE
     * writing only `resolver_hint` (rules-allowed on update): the deployed
     * row-UPDATED Eventarc handler flips `registry_ref.revision_source` to
     * "user" in place. The row doc survives — its id, `last_reviewed_at`,
     * comments and report-entry linkage are all preserved; nothing to warn
     * about, so callers show NO confirm dialog for this path.
     */
    async confirmRegulationEdition(
      pid: string,
      regulationId: string,
      familyId: string,
      revisionKey: string,
    ): Promise<void> {
      if (!pid || !regulationId || !familyId || !revisionKey) {
        logger.error('confirmRegulationEdition: missing pid, regulationId, familyId, or revisionKey')
        return
      }
      try {
        const firestore = useFirestore()
        await updateDoc(doc(firestore, `projects/${pid}/regulation_lists`, regulationId), {
          resolver_hint: { family_id: familyId, revision_key: revisionKey },
          updated_at: serverTimestamp(),
        })
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.edition_picker.confirm_inplace_success_title'),
          i18n.global.t('regulatory_monitoring.edition_picker.confirm_inplace_success'),
          'success',
        )
      } catch (error) {
        logger.error('confirmRegulationEdition failed', error)
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.edition_picker.pin_error_title'),
          i18n.global.t('regulatory_monitoring.edition_picker.pin_error'),
          'error',
        )
        throw error
      }
    },

    /**
     * MYAC-1496 lot 1 — confirm, in one gesture, the editions the resolver kept
     * by default on several rows (the post-import reminder). Same write as
     * `confirmRegulationEdition`, row by row in batches: only `resolver_hint`
     * naming the edition each row already carries, so the row-updated handler
     * flips `revision_source` to "user" in place. One toast for the whole set.
     */
    async confirmRegulationEditions(pid: string, rows: { id: string; familyId: string; revisionKey: string }[]): Promise<void> {
      if (!pid || rows.length === 0) return
      try {
        const firestore = useFirestore()
        for (let start = 0; start < rows.length; start += 450) {
          const batch = writeBatch(firestore)
          for (const row of rows.slice(start, start + 450)) {
            batch.update(doc(firestore, `projects/${pid}/regulation_lists`, row.id), {
              resolver_hint: { family_id: row.familyId, revision_key: row.revisionKey },
              updated_at: serverTimestamp(),
            })
          }
          await batch.commit()
        }
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.edition_picker.confirm_inplace_success_title'),
          i18n.global.t('regulatory_monitoring.import_defaulted_confirmed', { n: rows.length }, rows.length),
          'success',
        )
      } catch (error) {
        logger.error('confirmRegulationEditions failed', error)
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.edition_picker.pin_error_title'),
          i18n.global.t('regulatory_monitoring.edition_picker.pin_error'),
          'error',
        )
      }
    },

    /**
     * MYAC-1630 — the edition picker's choice on a row, ONE rule for every
     * picker (the norms table, the report card). The edition the row already
     * follows, resolved through the registry (a pin on an alias key, a row on
     * the family's old id), is a CONFIRMATION, written here with the pin as
     * stored (the server compares it as it is): returns `'confirmed'`. Any
     * other edition is a CHANGE that resets the review date: nothing is
     * written, the caller asks the user, then `pinRegulationRevision`;
     * returns `'change'`. `null` for a row unknown or without a family.
     */
    async chooseRegulationEdition(
      pid: string,
      regulationId: string,
      revisionKey: string,
    ): Promise<'confirmed' | 'change' | null> {
      const row = this.regulations.find((r) => r.id === regulationId)
      const familyId = row?.registry_ref?.family_id
      if (!pid || !row || !familyId) return null
      const pinned = row.registry_ref?.revision_key ?? null
      const families = useRegistryFamiliesStore()
      await families.resolveFamilies([familyId])
      if (pinned && families.sameEdition([familyId, pinned], [familyId, revisionKey])) {
        await this.confirmRegulationEdition(pid, regulationId, familyId, pinned)
        return 'confirmed'
      }
      return 'change'
    },

    async pinRegulationRevision(
      pid: string,
      regulationId: string,
      familyId: string,
      revisionKey: string,
      /** `silent`: no toast; the caller reports (the v3 finalisation lists
       *  each change with its own status). Errors still throw. */
      options: { silent?: boolean } = {},
    ): Promise<void> {
      if (!pid || !regulationId || !familyId || !revisionKey) {
        logger.error('pinRegulationRevision: missing pid, regulationId, familyId, or revisionKey')
        return
      }
      const row = this.regulations.find((r) => r.id === regulationId)
      if (!row) {
        logger.error('pinRegulationRevision: row not found', regulationId)
        return
      }
      // MYAC-1496 lot 1 (D10 bis): another edition of the family the row
      // already follows is changed IN PLACE. The front writes only
      // `resolver_hint`; the row-updated handler re-pins `registry_ref` on the
      // SAME doc (regulatory-monitoring `_change_inplace_edition_update`), so
      // the row keeps its id, `last_checked_at` and report linkage. Its
      // `last_reviewed_at` becomes the date of the change (decision B; the
      // previous value goes to `edition_history`), and going back is the same
      // gesture with the old edition, which restores it.
      // Only a pin onto ANOTHER family (a did-you-mean style re-pin) still
      // deletes and recreates the row below.
      // MYAC-1630: "the same family" is RESOLVED through `identifiers`: a row
      // stored on the family's old id (a merge since) pinned with the live id
      // is changed in place too; the server resolves the hint the same way.
      const families = useRegistryFamiliesStore()
      const storedFamilyId = row.registry_ref?.family_id ?? null
      if (storedFamilyId && storedFamilyId !== familyId) await families.resolveFamilies([storedFamilyId, familyId])
      if (families.sameFamily(storedFamilyId, familyId)) {
        try {
          const firestore = useFirestore()
          await updateDoc(doc(firestore, `projects/${pid}/regulation_lists`, regulationId), {
            // `requested_by`: the handler records who changed the edition in
            // the row's `edition_history` (Eventarc carries no actor).
            resolver_hint: { family_id: familyId, revision_key: revisionKey, requested_by: useUsersStore().uid },
            updated_at: serverTimestamp(),
          })
          if (!options.silent) {
            useAppStore().toast(
              i18n.global.t('regulatory_monitoring.edition_picker.pin_success_title'),
              i18n.global.t('regulatory_monitoring.edition_picker.pin_success'),
              'success',
            )
          }
        } catch (error) {
          logger.error('pinRegulationRevision (in place) failed', error)
          if (!options.silent) {
            useAppStore().toast(
              i18n.global.t('regulatory_monitoring.edition_picker.pin_error_title'),
              i18n.global.t('regulatory_monitoring.edition_picker.pin_error'),
              'error',
            )
          }
          throw error
        }
        return
      }
      if (await this._designationExists(pid, row.designation, regulationId)) {
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.duplicate_designation_title'),
          i18n.global.t('regulatory_monitoring.duplicate_designation', {
            designation: row.designation,
          }),
          'warn',
        )
        throw new Error('duplicate designation')
      }
      try {
        const firestore = useFirestore()
        const listRef = collection(firestore, `projects/${pid}/regulation_lists`)
        const oldRef = doc(firestore, `projects/${pid}/regulation_lists`, regulationId)
        const newRef = doc(listRef)
        const batch = writeBatch(firestore)
        batch.delete(oldRef)
        batch.set(newRef, {
          designation: row.designation,
          title: row.title ?? '',
          normative_body: row.normative_body ?? '',
          // Legacy rows (pre-MYAC-904) have no `as_typed` in the doc and reach
          // the store as plain objects without the model default — `undefined`
          // is rejected by Firestore. The designation IS what was once typed.
          as_typed: row.as_typed ?? row.designation,
          source: 'manual',
          resolver_hint: { family_id: familyId, revision_key: revisionKey },
          ...carriedCategories(row),
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        })
        await batch.commit()
        // Another family: a NEW row, so no history carries over; the toast
        // must not say it does.
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.edition_picker.pin_success_title'),
          i18n.global.t('regulatory_monitoring.edition_picker.pin_success_new_row'),
          'success',
        )
      } catch (error) {
        logger.error('pinRegulationRevision failed', error)
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.edition_picker.pin_error_title'),
          i18n.global.t('regulatory_monitoring.edition_picker.pin_error'),
          'error',
        )
        throw error
      }
    },

    /**
     * MYAC-1002 item 2 — follow the document that REPLACED this row.
     *
     * Same write shape as `pinRegulationRevision` above (one `writeBatch`, a
     * `regulation_lists` create carrying `resolver_hint` so the always-on
     * create trigger resolves the new row straight onto the successor), with
     * exactly one variable: whether the OLD row is deleted in the same batch.
     *   - `deleteSource: false` — "follow the new edition": the old row stays,
     *     so the tenant can keep watching the edition they still build under.
     *   - `deleteSource: true`  — "follow the new one and delete the old"
     *     (Aymeric's decision: delete, not keep-as-history).
     *
     * `last_reviewed_at` is NOT carried over — the create rule's field
     * allow-list doesn't accept it (see `pinRegulationRevision`'s note), and
     * the new row is a fresh subscription that has never been reviewed anyway.
     */
    async followSuccessorRegulation(
      pid: string,
      regulationId: string,
      data: {
        designation: string
        title: string
        normative_body: string
        family_id: string
        revision_key: string | null
      },
      /** `silent`: no toast, see `pinRegulationRevision`. Errors still throw. */
      options: { deleteSource: boolean; silent?: boolean },
    ): Promise<void> {
      const designation = data.designation.trim()
      if (!pid || !regulationId || !designation || !data.family_id) {
        logger.error('followSuccessorRegulation: missing pid, regulationId, designation, or family_id')
        return
      }
      const row = this.regulations.find((r) => r.id === regulationId)
      if (!row) {
        logger.error('followSuccessorRegulation: row not found', regulationId)
        return
      }
      // When the old row is deleted in the same batch it can never be the
      // duplicate — exclude it, exactly like `pinRegulationRevision` does.
      const excludeId = options.deleteSource ? regulationId : null
      if (await this._designationExists(pid, designation, excludeId)) {
        if (!options.silent) {
          useAppStore().toast(
            i18n.global.t('regulatory_monitoring.duplicate_designation_title'),
            i18n.global.t('regulatory_monitoring.duplicate_designation', { designation }),
            'warn',
          )
        }
        throw new Error('duplicate designation')
      }
      try {
        const firestore = useFirestore()
        const listRef = collection(firestore, `projects/${pid}/regulation_lists`)
        const batch = writeBatch(firestore)
        if (options.deleteSource) {
          batch.delete(doc(firestore, `projects/${pid}/regulation_lists`, regulationId))
        }
        batch.set(doc(listRef), {
          designation,
          title: data.title,
          normative_body: data.normative_body,
          as_typed: designation,
          source: 'manual',
          resolver_hint: { family_id: data.family_id, revision_key: data.revision_key },
          ...carriedCategories(row),
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        })
        await batch.commit()
        if (!options.silent) {
          useAppStore().toast(
            i18n.global.t('regulatory_monitoring.upgrade.success_title'),
            options.deleteSource
              ? i18n.global.t('regulatory_monitoring.upgrade.success_replaced', { designation })
              : i18n.global.t('regulatory_monitoring.upgrade.success_followed', { designation }),
            'success',
          )
        }
      } catch (error) {
        logger.error('followSuccessorRegulation failed', error)
        if (!options.silent) {
          useAppStore().toast(
            i18n.global.t('regulatory_monitoring.upgrade.error_title'),
            i18n.global.t('regulatory_monitoring.upgrade.error'),
            'error',
          )
        }
        throw error
      }
    },

    /**
     * MYAC-1607 — set the categories of one row (the client's own, free text).
     * `tags` and `tags_flat` are written together, always; no category left
     * writes the empty pair. No success toast: the chips change in place.
     */
    async setRegulationCategories(pid: string, regulationId: string, values: string[]): Promise<void> {
      if (!pid || !regulationId) {
        logger.error('setRegulationCategories: missing pid or regulationId')
        return
      }
      const row = this.regulations.find((r) => r.id === regulationId)
      if (row && sameCategories(categoriesOf(row.tags), values)) return
      try {
        const firestore = useFirestore()
        await updateDoc(doc(firestore, `projects/${pid}/regulation_lists`, regulationId), {
          ...categoryTagsPayload(values),
          updated_at: serverTimestamp(),
        })
      } catch (error) {
        logger.error('setRegulationCategories failed', error)
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.categories.save_error_title'),
          i18n.global.t('regulatory_monitoring.categories.save_error'),
          'error',
        )
        throw error
      }
    },

    /**
     * MYAC-1607 — add and remove categories on several rows at once, keeping
     * each row's other categories. Rows the edit leaves unchanged are not
     * written. Batched like `confirmRegulationEditions`; one toast.
     */
    async editRegulationsCategories(
      pid: string,
      regulationIds: string[],
      edit: { add: string[]; remove: string[] },
    ): Promise<void> {
      if (!pid || regulationIds.length === 0) {
        logger.error('editRegulationsCategories: missing pid or empty ids')
        return
      }
      const byId = new Map(this.regulations.map((r) => [r.id, r]))
      const writes: { id: string; values: string[] }[] = []
      for (const id of new Set(regulationIds)) {
        const row = byId.get(id)
        if (!row) continue
        const current = categoriesOf(row.tags)
        const next = applyCategoryEdit(current, edit)
        if (!sameCategories(current, next)) writes.push({ id, values: next })
      }
      try {
        const firestore = useFirestore()
        for (let start = 0; start < writes.length; start += 450) {
          const batch = writeBatch(firestore)
          for (const w of writes.slice(start, start + 450)) {
            batch.update(doc(firestore, `projects/${pid}/regulation_lists`, w.id), {
              ...categoryTagsPayload(w.values),
              updated_at: serverTimestamp(),
            })
          }
          await batch.commit()
        }
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.categories.bulk_success_title'),
          i18n.global.t('regulatory_monitoring.categories.bulk_success', { n: writes.length }, writes.length),
          'success',
        )
      } catch (error) {
        logger.error('editRegulationsCategories failed', error)
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.categories.save_error_title'),
          i18n.global.t('regulatory_monitoring.categories.save_error'),
          'error',
        )
        throw error
      }
    },

    /** MYAC-1607 — change the category filter or grouping of one surface. */
    setCategoryView(pid: string, surface: CategoryViewSurface, patch: Partial<CategoryViewState>): void {
      if (!pid) return
      const key = `${pid}:${surface}`
      const current = this.categoryViews[key] ?? EMPTY_CATEGORY_VIEW
      this.categoryViews = { ...this.categoryViews, [key]: { ...current, ...patch } }
    },

    /** MYAC-1607 — choose the list's optional columns (kept in table order). */
    setListColumns(pid: string, keys: readonly ListColumnKey[]): void {
      if (!pid) return
      this.listColumns = { ...this.listColumns, [pid]: LIST_COLUMNS.filter((k) => keys.includes(k)) }
    },

    /** MYAC-1691 — filter the list by the projects its standards apply to. */
    setListProjectsFilter(pid: string, values: readonly string[]): void {
      if (!pid) return
      this.listProjectsFilters = { ...this.listProjectsFilters, [pid]: [...values] }
    },

    /**
     * Delete one regulation row from regulation_lists.
     */
    async deleteRegulation(pid: string, regulationId: string): Promise<void> {
      if (!pid || !regulationId) {
        logger.error('deleteRegulation: missing pid or regulationId')
        return
      }
      try {
        const firestore = useFirestore()
        const ref = doc(firestore, `projects/${pid}/regulation_lists`, regulationId)
        await deleteDoc(ref)
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.delete_success_title'),
          i18n.global.t('regulatory_monitoring.delete_success'),
          'success',
        )
      } catch (error) {
        logger.error('deleteRegulation failed', error)
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.delete_error_title'),
          i18n.global.t('regulatory_monitoring.delete_error'),
          'error',
        )
        throw error
      }
    },

    async deleteRegulations(pid: string, regulationIds: string[]): Promise<void> {
      if (!pid || regulationIds.length === 0) {
        logger.error('deleteRegulations: missing pid or empty ids')
        return
      }
      try {
        const firestore = useFirestore()
        const batch = writeBatch(firestore)
        for (const id of regulationIds) {
          batch.delete(doc(firestore, `projects/${pid}/regulation_lists`, id))
        }
        await batch.commit()
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.delete_success_title'),
          i18n.global.t(
            'regulatory_monitoring.delete_selected_success',
            { count: regulationIds.length },
            regulationIds.length,
          ),
          'success',
        )
      } catch (error) {
        logger.error('deleteRegulations failed', error)
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.delete_error_title'),
          i18n.global.t('regulatory_monitoring.delete_error'),
          'error',
        )
        throw error
      }
    },

    subscribeEntries(pid: string, reportId: string): Promise<void> {
      if (!pid || !reportId) return Promise.resolve()
      this._cleanupEntriesSub()
      this.isLoadingEntries = true
      const firestore = useFirestore()
      const ref = collection(
        firestore,
        `projects/${pid}/regulation_reports/${reportId}/entries`,
      )
      return new Promise<void>((resolve) => {
        let resolved = false
        _entriesUnsub = onSnapshot(
          ref,
          (snap) => {
            this.reportEntries = snap.docs
              .map((d) => ReportEntry.fromFirebase(d))
              .filter((e): e is ReportEntry => e !== null)
            this.isLoadingEntries = false
            if (!resolved) { resolved = true; resolve() }
          },
          (error) => {
            logger.error('regulatoryMonitoring entries subscribe error', error)
            this.isLoadingEntries = false
            if (!resolved) { resolved = true; resolve() }
          },
        )
      })
    },

    /**
     * v3 counterpart (MYAC-1496 lot 1): the report's `cards` subcollection,
     * parsed through `ReportCardV3`. `cardsV3Status` tells a failed read from
     * an empty one, so the page never shows an empty report on an error.
     */
    subscribeCardsV3(pid: string, reportId: string): Promise<void> {
      if (!pid || !reportId) return Promise.resolve()
      this._cleanupCardsV3Sub()
      this.isLoadingEntries = true
      this.cardsV3Status = 'loading'
      const ref = collection(useFirestore(), `projects/${pid}/regulation_reports/${reportId}/cards`)
      this.positionsV3Status = 'loading'
      _positionsV3Unsub = onSnapshot(
        collection(useFirestore(), `projects/${pid}/regulation_reports/${reportId}/positions`),
        (snap) => {
          const out: Record<string, LinePositionV3> = {}
          for (const d of snap.docs) {
            const position = ReportPositionV3.fromFirebase(d)
            if (position) out[position.row_id || d.id] = position.asLine()
          }
          this.reportPositionsV3 = out
          this.positionsV3Status = 'ready'
        },
        (error) => {
          logger.error('regulatoryMonitoring positions (v3) subscribe error', error)
          this.positionsV3Status = 'error'
        },
      )
      return new Promise<void>((resolve) => {
        let resolved = false
        _cardsV3Unsub = onSnapshot(
          ref,
          (snap) => {
            this.reportCardsV3 = snap.docs
              .map((d) => ReportCardV3.fromFirebase(d))
              .filter((c): c is ReportCardV3 => c !== null)
            this.cardsV3Status = 'ready'
            this.isLoadingEntries = false
            if (!resolved) { resolved = true; resolve() }
          },
          (error) => {
            logger.error('regulatoryMonitoring cards (v3) subscribe error', error)
            this.cardsV3Status = 'error'
            this.isLoadingEntries = false
            if (!resolved) { resolved = true; resolve() }
          },
        )
      })
    },

    _cleanupCardsV3Sub() {
      if (_cardsV3Unsub) {
        _cardsV3Unsub()
        _cardsV3Unsub = null
      }
      if (_positionsV3Unsub) {
        _positionsV3Unsub()
        _positionsV3Unsub = null
      }
      this.reportCardsV3 = []
      this.reportPositionsV3 = {}
      this.positionsV3Status = 'idle'
      this.cardsV3Status = 'idle'
    },

    /**
     * v2 counterpart of `subscribeEntries` (MYAC-904 Phase 4) — parses docs
     * through `ReportEntryV2` instead of `ReportEntry` and writes to
     * `reportEntriesV2`. The report view calls this INSTEAD OF
     * `subscribeEntries` (single dispatch on the report doc's
     * `schema_version`), never both for the same report.
     */
    subscribeEntriesV2(pid: string, reportId: string): Promise<void> {
      if (!pid || !reportId) return Promise.resolve()
      this._cleanupEntriesV2Sub()
      this.isLoadingEntries = true
      const firestore = useFirestore()
      const ref = collection(
        firestore,
        `projects/${pid}/regulation_reports/${reportId}/entries`,
      )
      return new Promise<void>((resolve) => {
        let resolved = false
        _entriesV2Unsub = onSnapshot(
          ref,
          (snap) => {
            this.reportEntriesV2 = snap.docs
              .map((d) => ReportEntryV2.fromFirebase(d))
              .filter((e): e is ReportEntryV2 => e !== null)
            this.isLoadingEntries = false
            if (!resolved) { resolved = true; resolve() }
          },
          (error) => {
            logger.error('regulatoryMonitoring entries (v2) subscribe error', error)
            this.isLoadingEntries = false
            if (!resolved) { resolved = true; resolve() }
          },
        )
      })
    },

    /** Cache-first, never throws: uids already resolved (including ones that
     *  proved unreadable — cached as `null`) are skipped; an unresolvable uid
     *  is never surfaced raw — the card renders `review.reviewed_on`
     *  (name-less) for it instead. */
    async resolveReviewerNames(uids: string[]): Promise<void> {
      const missing = uids.filter((u) => !!u && !(u in this.reviewerNames))
      if (missing.length === 0) return
      const resolved = await fetchReviewerNames(useFirestore(), missing)
      // MYAC-1160 M8 — tombstone every requested uid, resolved or not: a
      // `null` entry is what makes the `!(u in this.reviewerNames)` filter
      // above skip an unreadable uid next time, instead of re-issuing its
      // `getDoc` on every re-fire.
      for (const uid of missing) this.reviewerNames[uid] = resolved.get(uid) ?? null
    },

    /** Ids of the watched rows as they exist NOW (live `regulation_lists`
     *  subscription, loaded by the router for every regulatory-monitoring page). */
    _liveRegulationIds(): Set<string> {
      return new Set(this.regulations.map((r) => r.id))
    },

    async submitReview(
      pid: string,
      reportId: string,
      entryId: string,
      decision: ReviewDecision,
      comment: string = '',
      regulationListDocId: string = '',
    ): Promise<void> {
      try {
        const firestore = useFirestore()
        const usersStore = useUsersStore()
        const entryRef = doc(
          firestore,
          `projects/${pid}/regulation_reports/${reportId}/entries/${entryId}`,
        )
        await updateDoc(entryRef, {
          review_decision: decision,
          review_comment: comment,
          reviewed_by: usersStore.uid,
          reviewed_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        })
        if (decision !== 'pending') { try { trackAction(CsmAction.RegulationMarkedSeen, { projectId: pid, projectMeta: { norm_count: this.regulations.length } }) } catch { } }
        if (comment.trim()) { try { trackAction(CsmAction.RegulationCommentAdded, { projectId: pid, projectMeta: { norm_count: this.regulations.length } }) } catch { } }

        // A row re-created since the report (edition pick, follow the new
        // version, designation fix) has a new id: updating the old one fails
        // with not-found after the verdict is already saved.
        // Only a real verdict dates the row: clearing it back to « pending »
        // is not a review (lot 1, 01/10).
        if (decision !== 'pending' && regulationListDocId && this._liveRegulationIds().has(regulationListDocId)) {
          const regRef = doc(firestore, `projects/${pid}/regulation_lists`, regulationListDocId)
          await updateDoc(regRef, {
            last_reviewed_at: serverTimestamp(),
            updated_at: serverTimestamp(),
          })
        }
      } catch (error) {
        logger.error('submitReview failed', error)
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.review.error_title'),
          i18n.global.t('regulatory_monitoring.review.error'),
          'error',
        )
      }
    },

    /**
     * v2 counterpart of `submitReview` (MYAC-904 Phase 4, plan "Review flow"
     * bullet): a v2 entry is per FAMILY, deduped across every subscriber row
     * that resolves to it — so marking it reviewed writes to ALL of
     * `subscription_row_ids`, not just one, in a single batch.
     *
     * MYAC-1160 review pass (NOW-5) — the write now SAYS so: until now
     * nothing on screen confirmed that a verdict or a comment had been
     * recorded.
     *
     * The row update stays exactly `last_reviewed_at` + `updated_at`.
     * NOW-9b would add `last_review_decision` / `last_reviewed_by` here, but
     * `firestore.rules:460` whitelists this row's updatable fields and an
     * unlisted field fails the WHOLE batch — including the entry update
     * above. Deferred until the rules are deployed (see the last section of
     * the plan).
     */
    async submitReviewV2(
      pid: string,
      reportId: string,
      entryId: string,
      decision: ReviewDecision,
      comment: string = '',
      subscriptionRowIds: string[] = [],
      origin: ReviewWriteOrigin = 'decision',
    ): Promise<void> {
      try {
        const firestore = useFirestore()
        const usersStore = useUsersStore()
        const batch = writeBatch(firestore)

        const entryRef = doc(
          firestore,
          `projects/${pid}/regulation_reports/${reportId}/entries/${entryId}`,
        )
        batch.update(entryRef, {
          review_decision: decision,
          review_comment: comment,
          reviewed_by: usersStore.uid,
          reviewed_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        })

        // Only rows that still exist: choosing an edition, following the new
        // version or fixing a designation deletes the row and re-creates it
        // under a NEW id, while the report's `subscription_row_ids` keep the
        // old one. One update on a missing doc fails the whole batch — the
        // verdict and the comment with it.
        //
        // Only a real verdict dates the rows (lot 1, 01/10): clearing it back
        // to « pending » or saving a comment alone is not a review, and the
        // list's « Revue le » must not move for it.
        const datesRows = origin === 'decision' && decision !== 'pending'
        const liveRowIds = this._liveRegulationIds()
        for (const rowId of subscriptionRowIds.filter((id) => datesRows && liveRowIds.has(id))) {
          const regRef = doc(firestore, `projects/${pid}/regulation_lists`, rowId)
          batch.update(regRef, {
            last_reviewed_at: serverTimestamp(),
            updated_at: serverTimestamp(),
          })
        }

        await batch.commit()

        if (decision !== 'pending') { try { trackAction(CsmAction.RegulationMarkedSeen, { projectId: pid, projectMeta: { norm_count: this.regulations.length } }) } catch { } }
        if (comment.trim()) { try { trackAction(CsmAction.RegulationCommentAdded, { projectId: pid, projectMeta: { norm_count: this.regulations.length } }) } catch { } }

        const detail =
          origin === 'comment'
            ? i18n.global.t('regulatory_monitoring.review.saved_comment')
            : decision === 'pending'
              ? i18n.global.t('regulatory_monitoring.review.saved_cleared')
              : i18n.global.t('regulatory_monitoring.review.saved_decision', {
                  decision: i18n.global.t(`regulatory_monitoring.review.decision.${decision}`),
                })
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.review.saved_title'),
          detail,
          'success',
        )
      } catch (error) {
        logger.error('submitReviewV2 failed', error)
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.review.error_title'),
          i18n.global.t('regulatory_monitoring.review.error'),
          'error',
        )
      }
    },

    async triggerRegulationWatch(pid: string, referenceDate?: string | null): Promise<void> {
      if (!pid) {
        logger.error('triggerRegulationWatch: missing pid')
        return
      }

      this._cleanupWatchCommandSub()

      try {
        const firestore = useFirestore()
        const commandsRef = collection(firestore, `projects/${pid}/command_queues/regulation_watch/commands`)

        const docRef = await addDoc(commandsRef, {
          status: 'requested',
          payload: {
            kind: 'regulation_watch',
            reference_date: referenceDate ?? null,
          },
          requested_by: null,
        })

        this._listenToWatchCommand(pid, docRef.id)
        try { trackAction(CsmAction.RegulationWatchTriggered, { projectId: pid, projectMeta: { norm_count: this.regulations.length } }) } catch { }
      } catch (error) {
        logger.error('Failed to trigger regulation_watch', error)
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.watch_error_title'),
          i18n.global.t('regulatory_monitoring.watch_error'),
          'error',
        )
        throw error
      }
    },
  },
})
